// Dates and times as the organization reads them: its locale, its timezone.
//
// INC-14a-3f (point 5): every hour of the field is written "HH:MM" in 24 hours
// ("12:13"), by the product's one shared format, and never with the locale's
// "a. m." or "p. m.", which the locale's own time style adds. The core of that
// format is here, below, where the field and the web both reach it; the web
// reads it through `shared/format.ts`, as it always did.
//
// INC-14a-3f-1: and every date of the field is written as the rest of the
// product writes it: "DD-MM" ("07-09"), "DD-MM-AAAA" where the year is needed,
// or in words where the field already used words ("martes 6 de octubre"),
// never in the locale's short form ("7/9/26"). No source of the field but this
// one asks the browser for a date (format.test.ts).

export interface Locality {
  locale: string;
  timeZone: string;
}

// --- the product's shared format, its core (moved from shared/format.ts) -----------------

/** The hyphen between the parts of a date, joined to what follows it. */
const DATE_HYPHEN = '-\u2060';

/** The parts of a date, the day first, as one piece no line breaks inside. */
export function dateText(...parts: string[]): string {
  return parts.join(DATE_HYPHEN);
}

/** The parts of an instant in the organization's timezone, with two digits. */
export function instantParts(
  value: string | number | Date,
  locality: Locality,
): Record<string, string> {
  const date = value instanceof Date ? value : new Date(value);
  const found: Record<string, string> = {};
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: locality.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    for (const part of formatter.formatToParts(date)) found[part.type] = part.value;
  } catch {
    // An unknown zone: the browser's own parts stand in.
    found.year = String(date.getFullYear());
    found.month = String(date.getMonth() + 1).padStart(2, '0');
    found.day = String(date.getDate()).padStart(2, '0');
    found.hour = String(date.getHours()).padStart(2, '0');
    found.minute = String(date.getMinutes()).padStart(2, '0');
  }
  return found;
}

/** "HH:MM" of an instant. */
export function clock(value: string | number | Date, locality: Locality): string {
  const found = instantParts(value, locality);
  return `${found.hour ?? '00'}:${found.minute ?? '00'}`;
}

/** "DD-MM" of an instant. */
export function dayMonth(value: string | number | Date, locality: Locality): string {
  const found = instantParts(value, locality);
  return dateText(found.day ?? '00', found.month ?? '00');
}

/** "DD-MM-AAAA" of a day given as a date, which is already the organization's. */
export function dayMonthYearOfDate(day: string): string {
  return dateText(day.slice(8, 10), day.slice(5, 7), day.slice(0, 4));
}

/** A day as the organization reads it: the ISO date in its timezone.
 *  `en-CA` formats a date as YYYY-MM-DD, which is what the API answers. */
export function localDate(instant: number | Date, timeZone: string): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

// --- the field's own ------------------------------------------------------------------------

/** A day and an hour, "DD-MM, HH:MM": when the last visit closed, when the
 *  last value was taken, when the day was last prepared. Each is recent work
 *  of the operator's, so the day goes without its year, as the product writes
 *  the day of recent work ("07-09"); until INC-14a-3f-1 it went in the locale's
 *  short form ("7/9/26"). */
export function formatDateTime(value: number | string | Date, locality: Locality): string {
  return `${dayMonth(value, locality)}, ${clock(value, locality)}`;
}

/** The time of day alone, as the header of a visit says when it started and
 *  the queue's detail when it tries again: the hour and the minute, "HH:MM",
 *  and never the seconds, which nobody reads off a bar. */
export function formatClock(value: number | string | Date, locality: Locality): string {
  return clock(value, locality);
}

/** A day the agenda names, in words.
 *
 *  A day is a date string and not an instant: it already is the day in the
 *  organization's reckoning, so it is read at UTC midnight and formatted
 *  there. Reading it in a zone behind UTC would move it to the day before. */
export function formatDay(
  day: string,
  locality: Locality,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' },
): string {
  try {
    return new Intl.DateTimeFormat(locality.locale, { ...options, timeZone: 'UTC' }).format(
      new Date(`${day}T00:00:00Z`),
    );
  } catch {
    return day;
  }
}

/** INC-14a-3a-2: the weekday, the day of the month and the month of a day, in
 *  the locale's words, for a text of the catalog that puts them in its own
 *  order: "sábado", "3", "octubre". */
export function dayParts(
  day: string,
  locality: Locality,
): { weekday: string; day: string; month: string } {
  const found = { weekday: '', day: '', month: '' };
  try {
    const parts = new Intl.DateTimeFormat(locality.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).formatToParts(new Date(`${day}T00:00:00Z`));
    for (const part of parts) {
      if (part.type === 'weekday' || part.type === 'day' || part.type === 'month') {
        found[part.type] = part.value;
      }
    }
  } catch {
    return { weekday: '', day, month: '' };
  }
  return found;
}

/** The same day with its weekday: what the title of the day reads, and since
 *  INC-14a-3f-1 each day of the week too, which read the month short ("6 oct")
 *  until then. "martes 6 de octubre", with no comma after the day of the week,
 *  as the product writes a day in words. */
export function formatDayLong(day: string, locality: Locality): string {
  return formatDay(day, locality, { weekday: 'long', day: 'numeric', month: 'long' }).replace(
    ',',
    '',
  );
}

/** The weekday and the day of the month, as the card of a visit names the day
 *  its plan is of: "martes 22", with no month, because the plan a visit comes
 *  from is always within the week the operator is standing in. */
export function formatWeekday(day: string, locality: Locality): string {
  return formatDay(day, locality, { weekday: 'long', day: 'numeric' });
}
